import * as THREE from 'three';
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';

/**
 * What a temporal upscaler gives a still picture, done without one: once the camera and
 * the scene stop, the frame is rendered a few more times with sub-pixel jitters and the
 * results are averaged in place, so edges lose their staircase and the ambient occlusion
 * its grain. Nothing changes while the picture moves; any change forgets the average.
 */
export interface StillRefinerHost {
  readonly renderer: THREE.WebGLRenderer;
  readonly camera: THREE.PerspectiveCamera;
  /** Renders the scene to the screen with the camera as it is. */
  drawFrame(): void;
}

export interface StillRefinerOptions {
  samples?: number;
  idleMs?: number;
  setTimeout?: (fn: () => void, ms: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
  requestFrame?: (fn: () => void) => unknown;
  cancelFrame?: (handle: unknown) => void;
  hidden?: () => boolean;
}

/** Halton(2, 3) − ½: sub-pixel offsets that stay evenly spread however many are used. */
export function jitterFor(index: number): [number, number] {
  return [halton(index + 1, 2) - .5, halton(index + 1, 3) - .5];
}
function halton(index: number, base: number): number {
  let f = 1, r = 0;
  while (index > 0) { f /= base; r += f * (index % base); index = Math.floor(index / base); }
  return r;
}

export class StillRefiner {
  samples = 0;
  enabled = true;
  readonly maxSamples: number;
  private readonly idleMs: number;
  private readonly timers: Required<Pick<StillRefinerOptions, 'setTimeout' | 'clearTimeout' | 'requestFrame' | 'cancelFrame' | 'hidden'>>;
  private timer: unknown = null;
  private frameHandle: unknown = null;
  private accumulation: THREE.WebGLRenderTarget | null = null;
  private screen = new THREE.FramebufferTexture(1, 1);
  private readonly size = new THREE.Vector2();
  private readonly blendMaterial: THREE.ShaderMaterial;
  private readonly blend: FullScreenQuad;
  private readonly presentMaterial: THREE.ShaderMaterial;
  private readonly present: FullScreenQuad;
  private jittered = false;
  private disposed = false;

  constructor(private readonly host: StillRefinerHost, options: StillRefinerOptions = {}) {
    this.maxSamples = options.samples ?? 16;
    this.idleMs = options.idleMs ?? 150;
    this.timers = {
      setTimeout: options.setTimeout ?? ((fn, ms) => setTimeout(fn, ms)),
      clearTimeout: options.clearTimeout ?? ((handle) => clearTimeout(handle as number)),
      requestFrame: options.requestFrame ?? ((fn) => requestAnimationFrame(fn)),
      cancelFrame: options.cancelFrame ?? ((handle) => cancelAnimationFrame(handle as number)),
      hidden: options.hidden ?? (() => typeof document !== 'undefined' && document.hidden),
    };
    this.screen.colorSpace = THREE.NoColorSpace;   // RGBA8, the canvas copy format
    this.blendMaterial = new THREE.ShaderMaterial({
      uniforms: { screen: { value: this.screen }, weight: { value: 1 } },
      transparent: true, blending: THREE.NormalBlending, depthTest: false, depthWrite: false, toneMapped: false,
      vertexShader: 'varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0., 1.); }',
      // The running average lives in linear light; the screen copy is decoded once here.
      fragmentShader: `uniform sampler2D screen; uniform float weight; varying vec2 vUv;
        void main() { gl_FragColor = vec4(sRGBTransferEOTF(texture2D(screen, vUv)).rgb, weight); }`,
    });
    this.blend = new FullScreenQuad(this.blendMaterial);
    this.presentMaterial = new THREE.ShaderMaterial({
      uniforms: { image: { value: null } },
      depthTest: false, depthWrite: false, toneMapped: false,
      vertexShader: 'varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0., 1.); }',
      // The include has to start its own line for three to resolve it.
      fragmentShader: `uniform sampler2D image; varying vec2 vUv;
        void main() {
          gl_FragColor = vec4(texture2D(image, vUv).rgb, 1.);
          #include <colorspace_fragment>
        }`,
    });
    this.present = new FullScreenQuad(this.presentMaterial);
  }

  /** The picture stopped changing: refine it after a short pause. */
  schedule(): void {
    if (this.disposed || !this.enabled || this.timer !== null || this.frameHandle !== null || this.samples > 0) return;
    this.timer = this.timers.setTimeout(() => { this.timer = null; this.step(); }, this.idleMs);
  }

  /** Something changed: stop and forget the average. */
  reset(): void {
    if (this.timer !== null) { this.timers.clearTimeout(this.timer); this.timer = null; }
    if (this.frameHandle !== null) { this.timers.cancelFrame(this.frameHandle); this.frameHandle = null; }
    this.samples = 0;
    if (this.jittered) { this.host.camera.clearViewOffset(); this.jittered = false; }
  }

  setEnabled(enabled: boolean): void { this.enabled = enabled; if (!enabled) this.reset(); }

  /** Draws the average onto the screen; false when there is none yet. */
  presentToScreen(): boolean {
    if (this.samples === 0 || !this.accumulation) return false;
    const renderer = this.host.renderer;
    renderer.setRenderTarget(null);
    this.present.render(renderer);
    return true;
  }

  supported(): boolean {
    const renderer = this.host.renderer;
    return !!renderer.capabilities?.isWebGL2 && !!renderer.extensions?.has('EXT_color_buffer_float');
  }

  private step(): void {
    if (this.disposed || !this.enabled || this.timers.hidden() || !this.supported()) return;
    const renderer = this.host.renderer, camera = this.host.camera;
    const size = renderer.getDrawingBufferSize(new THREE.Vector2());
    if (size.x < 1 || size.y < 1) return;
    if (!this.accumulation || !size.equals(this.size)) this.allocate(size);
    if (this.samples > 0) {
      const [x, y] = jitterFor(this.samples);
      camera.setViewOffset(size.x, size.y, x, y, size.x, size.y);
      this.jittered = true;
    }
    // Drawn and copied in one task: the canvas does not preserve its buffer between tasks,
    // so a copy of what was on screen a moment ago would be a black sample.
    this.host.drawFrame();
    renderer.setRenderTarget(null);
    renderer.copyFramebufferToTexture(this.screen);
    this.blendMaterial.uniforms.weight.value = 1 / (this.samples + 1);
    renderer.setRenderTarget(this.accumulation);
    if (this.samples === 0) renderer.clear();
    const autoClear = renderer.autoClear;
    renderer.autoClear = false;
    try { this.blend.render(renderer); } finally { renderer.autoClear = autoClear; renderer.setRenderTarget(null); }
    this.samples++;
    this.presentToScreen();
    if (this.samples < this.maxSamples) this.frameHandle = this.timers.requestFrame(() => { this.frameHandle = null; this.step(); });
    else if (this.jittered) { camera.clearViewOffset(); this.jittered = false; }   // the average is done; picking sees the plain projection
  }

  private allocate(size: THREE.Vector2): void {
    this.accumulation?.dispose();
    this.accumulation = new THREE.WebGLRenderTarget(size.x, size.y, {
      type: THREE.HalfFloatType, format: THREE.RGBAFormat, depthBuffer: false, stencilBuffer: false,
      minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, generateMipmaps: false,
    });
    this.accumulation.texture.colorSpace = THREE.NoColorSpace;
    this.presentMaterial.uniforms.image.value = this.accumulation.texture;
    this.screen.dispose();
    this.screen = new THREE.FramebufferTexture(size.x, size.y);
    this.screen.colorSpace = THREE.NoColorSpace;
    this.blendMaterial.uniforms.screen.value = this.screen;
    this.size.copy(size);
    this.samples = 0;
  }

  dispose(): void {
    this.reset();
    this.disposed = true;
    this.accumulation?.dispose(); this.accumulation = null;
    this.screen.dispose();
    this.blendMaterial.dispose(); this.blend.dispose();
    this.presentMaterial.dispose(); this.present.dispose();
  }
}
